/**
 * Wikidata "graph" fetcher — pulls related entities (P710 participant,
 * P361 part-of, P527 has-part, P155/P156 follows, etc.) for a given QID.
 *
 * Upstream: query.wikidata.org/sparql. Classifies related entities into
 * person/event/place/organization/other via P31 instance-of QIDs so the
 * relation panel in detail views can group them.
 *
 * No persistent cache — query is small and detail-view scoped.
 */
import type { Lang } from '../types/index.ts';
import type { SparqlResponse, SparqlBinding } from '../types/apiResponses.ts';
import { warnFetch } from '../utils/logErr.ts';

type RelationVar = 'prop' | 'related' | 'relatedLabel' | 'relatedDescription' | 'image' | 'typeQid' | 'articleUrl';
type RelationRow = Partial<Record<RelationVar, SparqlBinding>>;

export interface WikidataRelation {
  wikidataId: string;
  label: string;
  description?: string;
  relation: string;
  relationProperty: string;
  wikipediaUrl?: string;
  image?: string;
  entityType?: "person" | "event" | "place" | "organization" | "other";
}

// P31 (instance-of) QIDs → entity type classification
const PERSON_TYPES = new Set(["Q5", "Q729", "Q36180", "Q22808320"]);
const EVENT_TYPES = new Set(["Q1190554", "Q178561", "Q188055", "Q1261499", "Q350604", "Q13418847"]);
const PLACE_TYPES = new Set(["Q515", "Q532", "Q486972", "Q839954", "Q23413", "Q16970", "Q44539"]);
const ORG_TYPES = new Set(["Q43229", "Q4830453", "Q3918", "Q7278"]);

function classifyEntity(typeQids: string[]): WikidataRelation["entityType"] {
  for (const q of typeQids) {
    if (PERSON_TYPES.has(q)) return "person";
    if (EVENT_TYPES.has(q)) return "event";
    if (PLACE_TYPES.has(q)) return "place";
    if (ORG_TYPES.has(q)) return "organization";
  }
  return "other";
}

const RELATION_LABELS: Record<string, Record<string, string>> = {
  P710: { da: "Deltager", sv: "Deltagare", no: "Deltaker", en: "Participant", fi: "Osallistuja", is: "Þátttakandi" },
  P361: { da: "Del af", sv: "Del av", no: "Del av", en: "Part of", fi: "Osa", is: "Hluti af" },
  P527: { da: "Består af", sv: "Består av", no: "Består av", en: "Has part", fi: "Sisältää", is: "Samanstendur af" },
  P155: { da: "Efterfulgt af", sv: "Efterföljd av", no: "Etterfulgt av", en: "Followed by", fi: "Seurasi", is: "Á eftir" },
  P156: { da: "Fulgt efter", sv: "Följd efter", no: "Fulgt etter", en: "Follows", fi: "Edeltäjä", is: "Fyrir" },
  P17: { da: "Land", sv: "Land", no: "Land", en: "Country", fi: "Maa", is: "Land" },
  P131: { da: "Beliggende i", sv: "Belägen i", no: "Beliggende i", en: "Located in", fi: "Sijaitsee", is: "Staðsett í" },
  P137: { da: "Drevet af", sv: "Drivs av", no: "Drevet av", en: "Operated by", fi: "Ylläpitäjä", is: "Rekið af" },
  P170: { da: "Skaber", sv: "Skapare", no: "Skaper", en: "Creator", fi: "Tekijä", is: "Skapari" },
  P84: { da: "Arkitekt", sv: "Arkitekt", no: "Arkitekt", en: "Architect", fi: "Arkkitehti", is: "Arkitekt" },
  P1542: { da: "Har effekt", sv: "Har effekt", no: "Har effekt", en: "Has effect", fi: "Vaikutus", is: "Hefur áhrif" },
  P1478: { da: "Umiddelbar årsag", sv: "Omedelbar orsak", no: "Umiddelbar årsak", en: "Has immediate cause", fi: "Välitön syy", is: "Bein orsök" },
};

export async function fetchWikidataRelations(wikidataId: string, lang: Lang): Promise<WikidataRelation[]> {
  const langOrder = `${lang},en,da,sv,no,fi,is`;
  const properties = "P710 P361 P527 P155 P156 P131 P137 P170 P84 P1542 P1478".split(" ");
  const propValues = properties.map(p => `wdt:${p}`).join(" ");

  const query = `SELECT ?prop ?related ?relatedLabel ?relatedDescription ?image ?typeQid
    ?articleUrl WHERE {
    VALUES ?prop { ${propValues} }
    wd:${wikidataId} ?prop ?related .
    FILTER(ISIRI(?related))
    FILTER(STRSTARTS(STR(?related), "http://www.wikidata.org/entity/Q"))
    OPTIONAL { ?related wdt:P18 ?image }
    OPTIONAL { ?related wdt:P31 ?type . BIND(STRAFTER(STR(?type), "entity/") AS ?typeQid) }
    OPTIONAL { ?articleUrl schema:about ?related; schema:isPartOf/wikibase:wikiGroup "wikipedia" .
      FILTER(CONTAINS(STR(?articleUrl), "${lang}.wikipedia.org") || CONTAINS(STR(?articleUrl), "en.wikipedia.org")) }
    SERVICE wikibase:label { bd:serviceParam wikibase:language "${langOrder}" }
  } LIMIT 50`;

  try {
    const res = await fetch(
      `https://query.wikidata.org/sparql?format=json&query=${encodeURIComponent(query)}`,
      { headers: { Accept: "application/sparql-results+json" }, signal: AbortSignal.timeout(15000) }
    );
    if (!res.ok) return [];
    const data: SparqlResponse<RelationVar> = await res.json();
    const rows = data.results?.bindings ?? [];

    // Group by related entity to collect type QIDs
    const entityMap = new Map<string, { row: RelationRow; typeQids: string[]; prop: string }>();
    for (const r of rows) {
      const qid = r.related?.value?.split("/").pop();
      const propUri = r.prop?.value;
      const prop = propUri?.split("/").pop()?.replace("direct/", "") || "";
      if (!qid) continue;
      const existing = entityMap.get(qid);
      if (existing) {
        if (r.typeQid?.value) existing.typeQids.push(r.typeQid.value);
      } else {
        entityMap.set(qid, {
          row: r,
          typeQids: r.typeQid?.value ? [r.typeQid.value] : [],
          prop,
        });
      }
    }

    const relations: WikidataRelation[] = [];
    for (const [qid, { row, typeQids, prop }] of entityMap) {
      const label = row.relatedLabel?.value;
      if (!label || label === qid) continue; // Skip entities without labels
      const relationLabel = RELATION_LABELS[prop]?.[lang] || RELATION_LABELS[prop]?.["en"] || prop;

      relations.push({
        wikidataId: qid,
        label,
        description: row.relatedDescription?.value || undefined,
        relation: relationLabel,
        relationProperty: prop,
        wikipediaUrl: row.articleUrl?.value || undefined,
        image: row.image?.value ? row.image.value.replace(/^http:/, "https:") : undefined,
        entityType: classifyEntity(typeQids),
      });
    }

    return relations.slice(0, 20);
  } catch (e) {
    warnFetch("Failed to fetch Wikidata relations:", e);
    return [];
  }
}
